import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import {Dialog, DialogActions, DialogContent, Button, Paper, Typography, Box} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import LFCDataGrid from '_components/datagrid/LFCDataGrid';
import {GridColDef} from '@mui/x-data-grid-pro';
import type {InspectionRow} from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  row: InspectionRow | null;
  uuid: string;
  dsColumns?: GridColDef[]; // 画像ダイアログ下部グリッドと同じ列を利用するため受け取る
}

export default function BeadCrossSectionDialog({open, onClose, row, uuid, dsColumns}: Props) {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [startProcess, setStartProcess] = useState(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  // 取得データ（170）の生データ
  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  // ヘッダーは 169 の行(row)から表示するため、追加の取得は不要

  // 選択したビード（部位・枝番）の断面だけを取得する
  const map = row?.map ?? null;
  const branchNo = row?.branch_no ?? null;

  // データ取得（下段:170 の断面リスト）
  useEffect(() => {
    if (!open || !uuid) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');
    setStartProcess(true);
    const src = axios.CancelToken.source();
    cancelSource.current = src;

    const p170 = getLFCData({
      snack: enqueueSnackbar,
      sql_id: 170,
      // バックエンドSQLで未指定プレースホルダがあるため、null を明示送信
      parameters: {uuid, map, branch_no: branchNo, offset: null, limit: null},
      name: `${t('ビード断面判定結果_一覧')}`,
      cancelToken: src.token,
      t
    }).then((ds: any[]) => {
      setItems(Array.isArray(ds) ? ds : []);
    });

    Promise.allSettled([p170]).finally(() => setStartProcess(false));

    return () => {
      src.cancel('req cancel');
    };
  }, [open, uuid, map, branchNo, enqueueSnackbar, t]);

  if (!open || !row) return null;

  // 表示用データ（スクショ準拠の列構成）
  const columns: GridColDef[] = [
    {field: 'no', headerName: t('断面番号'), width: 90},
    {field: 'bead_eval', headerName: t('断面検査'), width: 120},
    {field: 'a_judge', headerName: t('A面形状判定'), width: 120},
    {field: 'a_leg', headerName: t('A面脚長'), width: 110},
    {field: 'a_under', headerName: t('A面アンダーカット深さ'), width: 160},
    {field: 'a_overlap', headerName: t('A面オーバーラップ高さ'), width: 170},
    {field: 'b_judge', headerName: t('B面形状判定'), width: 120},
    {field: 'b_leg', headerName: t('B面脚長'), width: 110},
    {field: 'b_under', headerName: t('B面アンダーカット深さ'), width: 160},
    {field: 'b_overlap', headerName: t('B面オーバーラップ高さ'), width: 170},
    {field: 'throat', headerName: t('のど厚さ'), width: 110},
    {field: 'haspit', headerName: t('ピット有無'), width: 110}
  ];

  type SectionRow = {
    id: string | number;
    no: unknown;
    bead_eval?: unknown;
    a_judge?: unknown;
    a_leg?: unknown;
    a_under?: unknown;
    a_overlap?: unknown;
    b_judge?: unknown;
    b_leg?: unknown;
    b_under?: unknown;
    b_overlap?: unknown;
    throat?: unknown;
    haspit?: unknown;
  };

  const rows: SectionRow[] = items.map((it, idx) => ({
    id: (it.id as string | number) ?? idx,
    no: it['ビード断面ID'] ?? it['bead_cross_section_id'] ?? idx + 1,
    bead_eval: it['ビード検査結果'] ?? it['bead_evaluation_label'] ?? it['bead_evaluation'],
    a_judge: it['A面 形状タイプ'] ?? it['a_plane_pattern_label'] ?? it['a_plane_pattern'],
    a_leg: it['A面 脚長'] ?? it['a_leg_length'],
    a_under: it['A面 アンダーカット距離'] ?? it['a_undercut_length'],
    a_overlap: it['A面 オーバーラップ距離'] ?? it['a_overlap_length'],
    b_judge: it['B面 形状タイプ'] ?? it['b_plane_pattern_label'] ?? it['b_plane_pattern'],
    b_leg: it['B面 脚長'] ?? it['b_leg_length'],
    b_under: it['B面 アンダーカット距離'] ?? it['b_undercut_length'],
    b_overlap: it['B面 オーバーラップ距離'] ?? it['b_overlap_length'],
    throat: it['のど厚'] ?? it['throat_length'],
    haspit:
      it['ピット有無'] ??
      (it['haspit'] === true
        ? t('ピットあり')
        : it['haspit'] === false
          ? t('ピットなし')
          : it['haspit'])
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // ダイアログを広く高く使う（画面に追従）
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '80vh',
          maxWidth: 'none'
        }
      }}
    >
      <LFCDialogTitle onClose={onClose}>{t('ビード断面詳細')}</LFCDialogTitle>
      <DialogContent dividers sx={{height: '100%', p: 0}}>
        <Box
          sx={{
            height: '100%',
            display: 'grid',
            gridTemplateRows: '20% 80%',
            rowGap: 0,
            '& .section': {minHeight: 0}
          }}
        >
          {/* 上段：ビード詳細（画像ダイアログ下部グリッドと同等） */}
          <Box className="section" sx={{p: 1, overflow: 'hidden'}}>
            <Typography
              variant="subtitle2"
              sx={{mt: 0, mb: 0.25, lineHeight: 1.2, fontWeight: 600}}
            >
              {t('ビード詳細')}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                height: 'calc(100% - 22px)',
                p: 0,
                '& .MuiDataGrid-row': {maxHeight: 36},
                '& .MuiDataGrid-columnHeaders': {minHeight: 36, maxHeight: 36, height: 36}
              }}
            >
              {!row || !dsColumns || dsColumns.length === 0 ? (
                <Box sx={{p: 1}}>{t('データがありません')}</Box>
              ) : (
                <LFCDataGrid
                  columns={(dsColumns || [])
                    .filter((c: GridColDef) => c.headerName !== 'rn')
                    .filter((c: GridColDef) => c.field !== 'bead_detail')}
                  rows={row ? [row] : []}
                  height="100%"
                  rowHeight={36}
                  pagination={false}
                  hideFooter={true}
                  exportFilename={`${t('ビード明細')}` as string}
                  columnOrder={[
                    {name: 'job_type_name', order: 0},
                    {name: 'map', order: 1},
                    {name: 'branch_no', order: 2}
                  ]}
                  style={{cursor: 'default'}}
                  initialState={{
                    left: ['job_type_name', 'map', 'branch_no', 'judge', 'img_filename'],
                    right: []
                  }}
                />
              )}
            </Paper>
          </Box>

          {/* 下段：ビード断面判定結果 */}
          <Box className="section" sx={{p: 1, overflow: 'hidden'}}>
            <Typography variant="subtitle2" sx={{mt: 0, mb: 0.5, lineHeight: 1.2, fontWeight: 600}}>
              {t('ビード断面判定結果')}
            </Typography>
            {startProcess && items.length === 0 ? (
              <Box sx={{p: 1}}>{t('読み込み中...')}</Box>
            ) : items.length === 0 ? (
              <Box sx={{p: 1}}>{t('データがありません')}</Box>
            ) : (
              <Paper variant="outlined" sx={{height: 'calc(100% - 22px)', p: 0}}>
                <LFCDataGrid
                  columns={columns}
                  rows={rows}
                  height="100%"
                  pageSize={50}
                  exportFilename={t('ビード断面判定結果') as string}
                  initialState={{left: ['no'], right: []}}
                />
              </Paper>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('閉じる')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
